/**
 * Gestión de plataforma — solo platform_admin.
 * Fase invite-only 2026-07-23. Guard client-side (redirect si no es
 * platform_admin) + server-side (endpoints exigen require_platform_admin).
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Shield, Users, Building2, AlertTriangle, Trash2 } from "lucide-react";

const ROLE_LABELS = {
  platform_admin: "Plataforma",
  admin: "Admin de cuenta",
  user: "Usuario",
};

export default function RadarPlatform() {
  const { user } = useAuth();
  const [tab, setTab] = useState("accounts");

  // Guard client-side (hooks primero, luego el redirect)
  if (user && user.role !== "platform_admin") {
    return <Navigate to="/radar" replace />;
  }

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <header className="mb-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700 mb-3 flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" /> Plataforma · Cuentas y usuarios
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Gestión de cuentas y usuarios
        </h1>
        <p className="font-body text-base text-slate-600 mt-2 max-w-2xl">
          Provisioná cuentas nuevas y usuarios (invite-only). Nadie entra si no está en esta lista —
          Google solo verifica identidad; la autorización la dás vos.
        </p>
      </header>

      <div className="flex border-b border-slate-200 mb-6">
        <TabButton active={tab === "accounts"} onClick={() => setTab("accounts")}
                   icon={Building2} label="Cuentas" testid="tab-accounts" />
        <TabButton active={tab === "users"} onClick={() => setTab("users")}
                   icon={Users} label="Usuarios" testid="tab-users" />
      </div>

      {tab === "accounts" ? <AccountsTab /> : <UsersTab />}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-amber-600 text-slate-900"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

// ── Tab: Cuentas ────────────────────────────────────────────────────

function AccountsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);  // {account, preview}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/accounts");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pude cargar las cuentas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post("/platform/accounts", { nombre: newName.trim(), plan: "free" });
      toast.success("Cuenta creada");
      setNewName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cuenta");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (acc) => {
    try {
      await api.patch(`/platform/accounts/${acc.account_id}`, { activa: !acc.activa });
      toast.success(acc.activa ? "Cuenta desactivada" : "Cuenta reactivada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const openDelete = async (acc) => {
    try {
      const r = await api.get(`/platform/accounts/${acc.account_id}/preview_delete`);
      setDeleteTarget({ account: acc, preview: r.data.counts });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pude preparar el borrado");
    }
  };

  return (
    <section>
      <form onSubmit={create} className="flex gap-2 mb-6" data-testid="form-crear-cuenta">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nombre de la nueva cuenta"
          data-testid="input-nueva-cuenta"
          className="max-w-sm"
        />
        <Button type="submit" disabled={creating || !newName.trim()}
                data-testid="btn-crear-cuenta"
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-none">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          <span className="ml-2">Nueva cuenta</span>
        </Button>
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-cuentas">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Nombre</th>
                <th className="px-4 py-3 text-left font-bold">account_id</th>
                <th className="px-4 py-3 text-right font-bold">Users</th>
                <th className="px-4 py-3 text-right font-bold">Zonas</th>
                <th className="px-4 py-3 text-center font-bold">Estado</th>
                <th className="px-4 py-3 text-right font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.account_id} className="border-t border-slate-100" data-testid={`row-cuenta-${a.account_id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{a.nombre}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{a.account_id}</td>
                  <td className="px-4 py-3 text-right font-mono">{a.users_count}</td>
                  <td className="px-4 py-3 text-right font-mono">{a.zonas_count}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 ${
                      a.activa ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                    }`}>{a.activa ? "Activa" : "Inactiva"}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => toggleActive(a)}
                            data-testid={`btn-toggle-cuenta-${a.account_id}`}
                            className="text-xs text-slate-600 hover:text-slate-900 underline">
                      {a.activa ? "Desactivar" : "Reactivar"}
                    </button>
                    <button onClick={() => openDelete(a)}
                            data-testid={`btn-delete-cuenta-${a.account_id}`}
                            className="ml-4 text-xs text-red-600 hover:text-red-800 underline inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400 italic">Sin cuentas</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <DeleteAccountModal
          account={deleteTarget.account}
          preview={deleteTarget.preview}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { setDeleteTarget(null); load(); }}
        />
      )}
    </section>
  );
}

function DeleteAccountModal({ account, preview, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const canDelete = confirm === account.nombre;

  const doDelete = async () => {
    setDeleting(true);
    try {
      const r = await api.delete(`/platform/accounts/${account.account_id}`, {
        data: { confirm_nombre: confirm },
      });
      const d = r.data.deleted_counts || {};
      toast.success(
        `Cuenta "${account.nombre}" eliminada — borrados: ${d.users || 0} users, ${d.zonas || 0} zonas, ${d.lotes || 0} lotes.`
      );
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  const total = (preview.users || 0) + (preview.zonas || 0) + (preview.lotes || 0)
              + (preview.opportunities || 0) + (preview.capturas || 0)
              + (preview.actividad || 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
         onClick={onClose}
         data-testid="modal-delete-account">
      <div className="bg-white max-w-lg w-full border-2 border-red-600"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-700 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading text-lg font-bold text-red-900">
              Eliminar cuenta &ldquo;{account.nombre}&rdquo;
            </h3>
            <p className="text-xs text-red-700 mt-0.5">Esto es <strong>irreversible</strong>. Los datos no se pueden recuperar.</p>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-slate-700 mb-3">Se borrarán en cascada:</p>
          <ul className="text-sm text-slate-800 space-y-1 mb-4 font-mono bg-slate-50 border border-slate-200 p-3">
            <li>· <strong>{preview.users}</strong> usuario{preview.users !== 1 && "s"} <span className="text-slate-500">(+ sus sesiones activas)</span></li>
            <li>· <strong>{preview.zonas}</strong> zona{preview.zonas !== 1 && "s"}</li>
            <li>· <strong>{preview.lotes.toLocaleString("es-AR")}</strong> lote{preview.lotes !== 1 && "s"} catastrales</li>
            <li>· <strong>{preview.opportunities.toLocaleString("es-AR")}</strong> aviso{preview.opportunities !== 1 && "s"} / oportunidades</li>
            <li>· <strong>{preview.capturas}</strong> captura{preview.capturas !== 1 && "s"} <span className="text-slate-500">y auditorías</span></li>
            <li>· <strong>{preview.actividad}</strong> registro{preview.actividad !== 1 && "s"} de actividad</li>
          </ul>
          {total === 0 && (
            <p className="text-xs text-slate-500 italic mb-4">La cuenta no tiene datos asociados.</p>
          )}

          <p className="text-sm text-slate-700 mb-2">
            Para confirmar, escribí el nombre exacto de la cuenta:
          </p>
          <p className="text-xs font-mono bg-slate-100 border border-slate-300 px-2 py-1 mb-2 inline-block">
            {account.nombre}
          </p>
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Escribí el nombre exacto"
            data-testid="input-confirm-delete-nombre"
            autoFocus
            disabled={deleting}
          />
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={deleting}
                  data-testid="btn-cancel-delete"
                  className="rounded-none">
            Cancelar
          </Button>
          <Button
            onClick={doDelete}
            disabled={!canDelete || deleting}
            data-testid="btn-confirm-delete"
            className="bg-red-600 hover:bg-red-700 text-white rounded-none disabled:bg-slate-300"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Eliminar definitivamente
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Usuarios ───────────────────────────────────────────────────

function UsersTab() {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", account_id: "", role: "user", name: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, a] = await Promise.all([
        api.get("/platform/users"),
        api.get("/platform/accounts"),
      ]);
      setItems(u.data.items || []);
      setAccounts(a.data.items || []);
      if (!form.account_id && a.data.items?.length) {
        setForm((f) => ({ ...f, account_id: a.data.items[0].account_id }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pude cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.account_id) return;
    setCreating(true);
    try {
      await api.post("/platform/users", {
        email: form.email.trim().toLowerCase(),
        account_id: form.account_id,
        role: form.role,
        name: form.name.trim() || undefined,
      });
      toast.success("Usuario provisionado — ya puede loguearse con Google");
      setForm({ email: "", account_id: form.account_id, role: "user", name: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al provisionar");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/platform/users/${u.user_id}`, { active: !u.active });
      toast.success(u.active ? "Usuario desactivado" : "Usuario reactivado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const accountName = (id) => accounts.find((a) => a.account_id === id)?.nombre || id;

  return (
    <section>
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-6" data-testid="form-provisionar-user">
        <Input
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="email@ejemplo.com"
          type="email"
          data-testid="input-user-email"
          className="md:col-span-2"
        />
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Nombre (opcional)"
          data-testid="input-user-name"
        />
        <select
          value={form.account_id}
          onChange={(e) => setForm({ ...form, account_id: e.target.value })}
          data-testid="select-user-account"
          className="h-10 border border-slate-300 bg-white px-3 text-sm"
        >
          {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.nombre}</option>)}
        </select>
        <div className="flex gap-2">
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="select-user-role"
            className="h-10 border border-slate-300 bg-white px-3 text-sm flex-1"
          >
            <option value="user">Usuario</option>
            <option value="admin">Admin cuenta</option>
            <option value="platform_admin">Plataforma</option>
          </select>
          <Button type="submit" disabled={creating || !form.email.trim() || !form.account_id}
                  data-testid="btn-provisionar-user"
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-none px-3">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-usuarios">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Email</th>
                <th className="px-4 py-3 text-left font-bold">Nombre</th>
                <th className="px-4 py-3 text-left font-bold">Cuenta</th>
                <th className="px-4 py-3 text-left font-bold">Rol</th>
                <th className="px-4 py-3 text-left font-bold">Último acceso</th>
                <th className="px-4 py-3 text-center font-bold">Estado</th>
                <th className="px-4 py-3 text-right font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.user_id} className="border-t border-slate-100" data-testid={`row-user-${u.user_id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.email}</td>
                  <td className="px-4 py-3 text-slate-600">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{accountName(u.account_id)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 ${
                      u.role === "platform_admin" ? "bg-amber-100 text-amber-800" :
                      u.role === "admin" ? "bg-emerald-100 text-emerald-800" :
                      "bg-slate-100 text-slate-600"
                    }`}>{ROLE_LABELS[u.role] || u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 ${
                      u.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                    }`}>{u.active ? "Activo" : "Inactivo"}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => toggleActive(u)}
                            data-testid={`btn-toggle-user-${u.user_id}`}
                            className="text-xs text-slate-600 hover:text-slate-900 underline">
                      {u.active ? "Desactivar" : "Reactivar"}
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400 italic">Sin usuarios</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
